import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {ResizeObserver | null} */
  #headerResizeObserver = null;

  /** @type {HTMLElement | null} */
  #observedHeader = null;

  /** @type {ResizeObserver | null} */
  #summaryResizeObserver = null;

  /** @type {HTMLElement | null} */
  #observedSummary = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogCloseEvent.eventName, this.#unobserveSummary);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogCloseEvent.eventName, this.#unobserveSummary);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    this.#headerResizeObserver?.disconnect();
    this.#observedHeader = null;
    this.#summaryResizeObserver?.disconnect();
    this.#observedSummary = null;
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    this.#observeHeader(dialog);
    this.#updateHeaderHeight(dialog);

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    this.#observeSummary(summary);

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  /**
   * DD-FIX: the sticky state used to be measured only on open, so quantity
   * changes, removals, or discount pills appearing while the drawer was open
   * left it stale. Re-measure whenever the summary's size changes; the
   * observer is released on close so a hidden drawer costs nothing.
   * @param {Element | null} summary
   */
  #observeSummary(summary) {
    const element = summary instanceof HTMLElement ? summary : null;

    if (this.#observedSummary === element) return;

    this.#summaryResizeObserver?.disconnect();
    this.#observedSummary = element;

    if (!element) return;

    this.#summaryResizeObserver ??= new ResizeObserver(() => this.#updateStickyState());
    this.#summaryResizeObserver.observe(element);
  }

  #unobserveSummary = () => {
    this.#summaryResizeObserver?.disconnect();
    this.#observedSummary = null;
  };

  /**
   * Tracks the cart drawer header height so the scroll area can size correctly
   * when promo pills wrap onto another line.
   * @param {HTMLDialogElement} dialog
   */
  #observeHeader(dialog) {
    const header = dialog.querySelector('#cart-drawer-header');

    if (this.#observedHeader === header) return;

    this.#headerResizeObserver?.disconnect();
    this.#observedHeader = header instanceof HTMLElement ? header : null;

    if (!this.#observedHeader) return;

    this.#headerResizeObserver ??= new ResizeObserver(() => this.#updateHeaderHeight(dialog));
    this.#headerResizeObserver.observe(this.#observedHeader);
  }

  /**
   * @param {HTMLDialogElement} dialog
   */
  #updateHeaderHeight(dialog) {
    const header = dialog.querySelector('#cart-drawer-header');
    const content = dialog.querySelector('.cart-drawer__content');

    if (!(header instanceof HTMLElement) || !(content instanceof HTMLElement)) return;

    const headerHeight = Math.round(header.getBoundingClientRect().height);
    content.style.setProperty('--header-height', `${headerHeight}px`);
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
